import {
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
} from 'react-phone-number-input'
const {
  constants: {
    STRING_ACCOUNT_NUMBER,
    STRING_ROUTING_NUMBER_WIRE,
    //STRING_ROUTING_NUMBER_EFT,
    STRING_BENEFICIARY_NAME,
    STRING_ROUTING_NUMBER_ACH,
    STRING_NAME,
    CARD_CVV,
    CARD_YEAR,
    CARD_MONTH,
  },
  Validator,
} = require('@plastiq/external-api-validation')
import { convertStringToNumber } from './helpers'

const NUMBER_PAYMENT_AMOUNT = {
  minNumber: 1.0,
  maxNumber: 9999999999.99,
}

const STRING_ACCOUNT_NAME = {
  format: 'ACCOUNT_NAME',
  maxLength: 64,
  minLength: 1,
}

const STRING_INVOICE_NUMBER = {
  format: 'INVOICE_NUMBER',
  maxLength: 25,
  minLength: 1,
}

const externalValidationWrapper = (
  validationFunction: Function,
  errorMsg: string,
) => {
  try {
    validationFunction()
    return true
  } catch (e) {
    return errorMsg
  }
}

const isPossibleNumber = (phoneNumber: string) => {
  if (!phoneNumber) {
    return true
  }
  const intlPhoneNumber =
    phoneNumber.charAt(0) === '+' ? phoneNumber : `+${phoneNumber}`
  const formattedPhoneNumber = formatPhoneNumberIntl(intlPhoneNumber)

  return isPossiblePhoneNumber(formattedPhoneNumber)
}

const validators = {
  paymentDetailsAccountName: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(
          val,
          'paymentDetailsAccountName',
          STRING_ACCOUNT_NAME,
        ),
      'Enter a valid Account Name',
    )
  },
  accountNumber: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(val, 'accountNumber', STRING_ACCOUNT_NUMBER),
      'Enter a valid Account Number',
    )
  },
  amount: (val: string) => {
    const value = convertStringToNumber(val)
    if (!value) {
      return 'Amount field is required'
    }
    return externalValidationWrapper(
      () =>
        Validator.validateNumber(value, 'amountInput', NUMBER_PAYMENT_AMOUNT),
      'Enter a valid Amount between 1 and 99,999,999.99',
    )
  },
  paymentDetailsAccountNumber: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(
          val,
          'paymentDetailsInvoiceNumber',
          STRING_INVOICE_NUMBER,
        ),
      'Enter a valid Invoice Number',
    )
  },
  beneficiaryName: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(
          val.trim(),
          'beneficiaryName',
          STRING_BENEFICIARY_NAME,
        ),
      'Enter a valid Beneficiary Name',
    )
  },
  name: (label?: string) => (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () => Validator.validateString(val.trim(), label || 'name', STRING_NAME),
      `Enter a valid ${label || label}`,
    )
  },
  emailFormat: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(val, 'email', {
          format: 'EMAIL',
        }),
      'Enter a valid Email Address',
    )
  },
  phoneFormat: (val: string) => {
    if (!val) {
      return true
    }
    return isPossibleNumber(val) ? true : 'Enter a valid phone number'
  },
  ACHRoutingNumber: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(
          val,
          'routingNumber',
          STRING_ROUTING_NUMBER_ACH,
        ),
      'Enter a valid ACH Routing Number',
    )
  },
  WireRoutingNumber: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () =>
        Validator.validateString(
          val,
          'WireRoutingNumber',
          STRING_ROUTING_NUMBER_WIRE,
        ),
      'Enter a valid Wire Routing Number',
    )
  },
  validateAddress: (val: string) => {
    const INVALID_ADDRESS_CHARS_REGEX = /[`~!@$%^*_=[{\]}\\|;"<>?]/
    if (!val) {
      return true
    }
    const isValidAddress = !INVALID_ADDRESS_CHARS_REGEX.test(val)
    return isValidAddress || 'Enter a valid address'
  },
  zipOrPostalCode:
    (
      country: string,
      countryRequired: boolean = false,
      postalCodeRequired: boolean = false,
    ) =>
    (val: string) => {
      if (!country && (postalCodeRequired || val)) {
        return 'Please select a country'
      }
      if (!val && !postalCodeRequired) {
        return true
      }

      return externalValidationWrapper(
        () => Validator.validatePostalCode(val, country, 'postalCode'),
        country !== 'US'
          ? 'Enter a valid Postal Code'
          : 'Enter a valid Zip Code',
      )
    },
  validateCard: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () => Validator.validateCard(val.replace(/\D/g, ''), 'cardNumber'),
      'Enter a valid Card Number',
    )
  },
  validateExp: (val: string) => {
    if (!val) {
      return true
    }
    const [month, year] = val.split('/')
    return externalValidationWrapper(
      () =>
        Validator.validateString(month, 'month', CARD_MONTH) &&
        Validator.validateString(year, 'year', CARD_YEAR),
      'Enter a valid expiration date',
    )
  },
  validateCVV: (val: string) => {
    if (!val) {
      return true
    }
    return externalValidationWrapper(
      () => Validator.validateString(val, 'cvv', CARD_CVV),
      'Enter a valid CVV',
    )
  },
}

export { validators }
